import { useEffect } from "react";
import { supabase } from "../lib/supabase";
import { getDeviceFingerprint } from "../utils/deviceFingerprint";
import { getDeviceName } from "../utils/deviceName";

export function useDeviceCheck(user: any) {
  useEffect(() => {
    if (!user) return;

    async function checkDevice() {

      // Get profile
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("role, status, max_devices")
        .eq("id", user.id)
        .single();

      if (profileError || !profile) return;

      // Only apply to clients
      if (profile.role !== "client") return;

      // If suspended/pending → handled elsewhere
      if (profile.status !== "active") return;

      // Generate fingerprint
      const fingerprint = await getDeviceFingerprint();

      // Get user devices
      const { data: devices, error: devicesError } = await supabase
        .from("devices")
        .select("*")
        .eq("user_id", user.id);

      if (devicesError) return;

      // FIRST LOGIN (no devices yet)
      if (!devices || devices.length === 0) {

        await supabase.from("devices").insert({
          user_id: user.id,
          fingerprint,
          device_name: getDeviceName(),
          approved: true
        });

        return;
      }

      // Check if fingerprint already exists
      const existing = devices.find(d => d.fingerprint === fingerprint);

      // Existing approved device → allow
      if (existing && existing.approved) return;

      // Existing device but not approved
      if (existing && !existing.approved) {

        // Check if admin approved it
        const { data: refreshed } = await supabase
          .from("devices")
          .select("approved")
          .eq("id", existing.id)
          .single();

        if (refreshed?.approved) return;

        await suspend(user.id);
        return;
      }

      // New device detected
      const approvedDevices = devices.filter(d => d.approved);

      // Insert new device
      await supabase.from("devices").insert({
        user_id: user.id,
        fingerprint,
        device_name: getDeviceName(),
        approved: false
      });

      // If device limit reached → suspend
      if (approvedDevices.length >= profile.max_devices) {
        await suspend(user.id);
        return;
      }

    }

    async function suspend(userId: string) {

      await supabase
        .from("profiles")
        .update({ status: "suspended" })
        .eq("id", userId);

      window.location.reload();
    }

    checkDevice();

  }, [user?.id]);
}